/*
 * Minimal browser error reporting for the form (hosted page + embeds).
 *
 * Deliberately NOT the Sentry SDK: the embed bundle runs on partner websites,
 * so it must stay small and must never install global error handlers that
 * would swallow or report the HOST page's errors. We report only from our own
 * code paths: API-call failures (submit/bank-session/draft) and render crashes
 * caught by our own React error boundary.
 *
 * Activates when VITE_SENTRY_DSN is baked into the build; otherwise every call
 * is a no-op. Auth travels in the query string so requests need no custom
 * headers (no CORS preflight). Never sends form data — only error class,
 * message, endpoint URL and status. Page URLs are stripped of query strings
 * (resume links carry tokens).
 */
const DSN = String(import.meta.env.VITE_SENTRY_DSN || "").trim();

let target = null; // { url } once parsed
let sent = 0;
let surfaceTag = "form"; // refined by initErrorReporting at mount
const MAX_PER_PAGE = 8;

/* Called once from each entry point: "form-hosted" (our page) or
   "form-embed" (partner sites). */
export function initErrorReporting(surface) {
  surfaceTag = surface || surfaceTag;
}

function ingestUrl() {
  if (target) return target.url;
  try {
    const u = new URL(DSN);
    const projectId = u.pathname.replace(/\/+/g, "");
    if (!u.username || !projectId) return null;
    target = { url: `${u.protocol}//${u.host}/api/${projectId}/envelope/?sentry_version=7&sentry_key=${u.username}&sentry_client=brokerage-form%2F1.0` };
    return target.url;
  } catch {
    return null;
  }
}

const uuid = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(/[^a-z0-9]/gi, "").padEnd(32, "0").slice(0, 32);

/* context: { where, url, status } — never form data. */
export function reportError(err, context = {}) {
  try {
    const url = ingestUrl();
    if (!url || sent >= MAX_PER_PAGE) return;
    sent += 1;
    const event = {
      event_id: uuid(),
      timestamp: new Date().toISOString(),
      platform: "javascript",
      level: "error",
      environment: String(import.meta.env.VITE_SENTRY_ENVIRONMENT || "unlabelled"),
      request: { url: location.origin + location.pathname }, // no query - resume links carry tokens
      tags: { surface: surfaceTag, host: location.host },
      extra: {
        where: context.where,
        endpoint: context.url,
        status: context.status,
      },
      exception: {
        values: [{
          type: String((err && err.name) || "Error"),
          value: String((err && err.message) || err || "unknown").slice(0, 500),
          ...(err && err.stack ? { stacktrace: { frames: parseFrames(err.stack) } } : {}),
        }],
      },
    };
    const envelope =
      JSON.stringify({ event_id: event.event_id, sent_at: event.timestamp }) + "\n" +
      JSON.stringify({ type: "event" }) + "\n" +
      JSON.stringify(event) + "\n";
    fetch(url, { method: "POST", body: envelope, keepalive: true }).catch(() => {});
  } catch {
    /* reporting must never disturb an applicant */
  }
}

function parseFrames(stack) {
  const frames = [];
  for (const line of String(stack).split("\n")) {
    const m = line.match(/(?:at\s+(?:(.+?)\s+\()?|@)([^\s()]+?):(\d+):(\d+)\)?\s*$/);
    if (!m) continue;
    frames.push({ function: m[1] || "<anonymous>", filename: m[2], lineno: parseInt(m[3], 10), colno: parseInt(m[4], 10), in_app: true });
  }
  return frames.reverse();
}

export const errorReportingEnabled = () => !!DSN;
